import { evaluate } from './flip';
import { daysBetween } from './format';
import type { Car } from './types';

export interface Stats {
  soldCount: number;
  realisedProfit: number;
  averageProfit: number;
  /** Share of sold cars that made money, 0 to 100. */
  winRatePct: number;
  /** Average days from buying to selling, over cars with both dates. */
  averageDays: number | null;
  best: { title: string; profit: number } | null;
  worst: { title: string; profit: number } | null;
  /** Actual profit minus planned profit over sold cars. Negative means estimates were too hopeful. */
  versusPlan: number;
  openCount: number;
  /** Cash currently sitting in cars you have bought and not sold. */
  cashTiedUp: number;
  /** Profit you expect from those cars if they sell as currently entered. */
  openExpectedProfit: number;
  watchingCount: number;
}

const OPEN = new Set(['bought', 'repairing', 'listed']);

export function computeStats(cars: readonly Car[]): Stats {
  const sold = cars.filter((c) => c.status === 'sold');
  const open = cars.filter((c) => OPEN.has(c.status));
  const soldResults = sold.map((c) => ({ c, r: evaluate(c.deal), planned: evaluate(c.planned) }));
  const realisedProfit = soldResults.reduce((s, x) => s + x.r.profit, 0);
  const days = sold.map((c) => daysBetween(c.boughtOn, c.soldOn)).filter((d): d is number => d !== null);
  const byProfit = [...soldResults].sort((a, b) => b.r.profit - a.r.profit);
  const pick = (x?: { c: Car; r: { profit: number } }) => (x ? { title: x.c.title, profit: x.r.profit } : null);
  const openResults = open.map((c) => evaluate(c.deal));
  return {
    soldCount: sold.length,
    realisedProfit,
    averageProfit: sold.length ? realisedProfit / sold.length : 0,
    winRatePct: sold.length ? (soldResults.filter((x) => x.r.profit > 0).length / sold.length) * 100 : 0,
    averageDays: days.length ? days.reduce((a, b) => a + b, 0) / days.length : null,
    best: pick(byProfit[0]),
    worst: pick(byProfit.length > 1 ? byProfit[byProfit.length - 1] : undefined),
    versusPlan: soldResults.reduce((s, x) => s + (x.r.profit - x.planned.profit), 0),
    openCount: open.length,
    cashTiedUp: openResults.reduce((s, r) => s + r.cashInvested, 0),
    openExpectedProfit: openResults.reduce((s, r) => s + r.profit, 0),
    watchingCount: cars.filter((c) => c.status === 'watching').length,
  };
}
